import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchAllContentsByPaginating } from '../services/productsService';
import {
  fetchChecklists,
  fetchChecklistById,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  deleteChecklistPermanently,
  assignChecklistToProducts,
  fetchChecklistAssignedProducts,
  removeProductFromChecklist,
} from '../services/checklistsService';
import { useConfirmDialog } from '../contexts/ConfirmContext';

const CHECKLIST_ISLEM_TIPLERI = ['Kurulum', 'Bakım', 'Arıza'];
import { includesNormalized } from '../utils/searchHelpers';
import './ChecklistsPage.css';

const notify = (type, msg) => {
  if (window.showNotification) window.showNotification(type, msg);
  else if (type === 'error') console.error(msg);
  else console.log(msg);
};

function durumEtiketi(row) {
  if (row.silindi) return { kisa: 'Silinmiş', sinif: 'badge-archive' };
  if (!row.aktif) return { kisa: 'Pasif', sinif: 'badge-inactive' };
  return { kisa: 'Aktif', sinif: 'badge-active' };
}

function formatTarih(raw) {
  if (raw == null || raw === '') return '—';
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function ChecklistsPage() {
  const { confirm } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  /** @type {string|null|'new'} */
  const [editorTargetId, setEditorTargetId] = useState(null);

  const [formAd, setFormAd] = useState('');
  const [formIslemTipi, setFormIslemTipi] = useState('Kurulum');
  const [formAktif, setFormAktif] = useState(true);
  const [formSorular, setFormSorular] = useState([]);
  const [archivedSorular, setArchivedSorular] = useState([]);
  const [editingSilindi, setEditingSilindi] = useState(false);
  const [saving, setSaving] = useState(false);

  const [urunler, setUrunler] = useState([]);
  const [urunLoading, setUrunLoading] = useState(false);
  const [urunArama, setUrunArama] = useState('');
  const [selectedUrunIds, setSelectedUrunIds] = useState(() => new Set());
  const [atanmisUrunler, setAtanmisUrunler] = useState([]);
  const [atanmisLoading, setAtanmisLoading] = useState(false);
  /** @type {'sablon' | 'atama' | 'eklenen'} */
  const [modalTab, setModalTab] = useState('sablon');

  const editorOpenRef = useRef(false);
  const editorTargetIdRef = useRef(null);

  const effectiveChecklistId =
    editorTargetId && editorTargetId !== 'new' ? editorTargetId : null;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchChecklists();
      if (!res?.basarili) throw new Error(res?.mesaj || 'Liste alınamadı');
      setList(Array.isArray(res.veri) ? res.veri : []);
    } catch (e) {
      notify('error', e.message || 'Checklist listesi yüklenemedi');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    editorOpenRef.current = editorOpen;
    editorTargetIdRef.current = editorTargetId;
  });

  const openEditor = useCallback(
    (id, opts = {}) => {
      const isNew = id === 'new';
      setModalTab('sablon');
      setEditorOpen(true);
      setEditorTargetId(isNew ? 'new' : String(id));
      if (!opts.skipUrl && !isNew) {
        setSearchParams({ checklist: String(id) }, { replace: true });
      }
      if (isNew) {
        setSearchParams({}, { replace: true });
        setFormAd('');
        setFormIslemTipi('Kurulum');
        setFormAktif(true);
        setFormSorular([{
          _id: undefined,
          sira: 0,
          metin: '',
          cagriMerkezi: true,
          teknikServis: true,
          degerAlani: false,
          degerBirimi: '',
        }]);
        setArchivedSorular([]);
        setEditingSilindi(false);
        setSelectedUrunIds(new Set());
        setAtanmisUrunler([]);
      }
    },
    [setSearchParams],
  );

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditorTargetId(null);
    setFormAd('');
    setFormIslemTipi('Kurulum');
    setFormAktif(true);
    setFormSorular([]);
    setArchivedSorular([]);
    setEditingSilindi(false);
    setSelectedUrunIds(new Set());
    setAtanmisUrunler([]);
    setModalTab('sablon');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  /** Yalnızca URL değişince — editorOpen bağımlılığı kaldırıldı (ilk tıkta kapanma hatası önlenir). */
  useEffect(() => {
    const fromUrl = searchParams.get('checklist');
    if (!fromUrl) return;
    if (
      editorOpenRef.current &&
      String(editorTargetIdRef.current) === String(fromUrl)
    ) {
      return;
    }
    openEditor(fromUrl, { skipUrl: true });
  }, [searchParams, openEditor]);

  const loadAtanmisUrunler = useCallback(async (checklistId) => {
    if (!checklistId) {
      setAtanmisUrunler([]);
      return;
    }
    setAtanmisLoading(true);
    try {
      const res = await fetchChecklistAssignedProducts(checklistId);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Liste alınamadı');
      setAtanmisUrunler(Array.isArray(res.veri) ? res.veri : []);
    } catch (e) {
      notify('error', e.message || 'Atanmış ürünler yüklenemedi');
      setAtanmisUrunler([]);
    } finally {
      setAtanmisLoading(false);
    }
  }, []);

  useEffect(() => {
    if (effectiveChecklistId) loadAtanmisUrunler(effectiveChecklistId);
    else {
      setAtanmisUrunler([]);
    }
  }, [effectiveChecklistId, loadAtanmisUrunler]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUrunLoading(true);
      try {
        const all = await fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true });
        if (!cancelled) setUrunler(Array.isArray(all) ? all : []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setUrunLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDetail = async (id) => {
    if (!id || id === 'new') return;
    try {
      const res = await fetchChecklistById(id);
      if (!res?.basarili || !res.veri) throw new Error(res?.mesaj || 'Bulunamadı');
      const d = res.veri;
      setFormAd(d.ad || '');
      setFormIslemTipi(d.islemTipi || 'Kurulum');
      setFormAktif(d.aktif !== false);
      setEditingSilindi(d.silindi === true);
      const all = d.sorular || [];
      const aktif = all
        .filter((s) => !s.pasif)
        .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
      const ars = all
        .filter((s) => s.pasif)
        .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
      setFormSorular(
        aktif.map((s) => ({
          _id: s._id,
          sira: s.sira,
          metin: s.metin || '',
          cagriMerkezi: s.cagriMerkezi !== false,
          teknikServis: s.teknikServis !== false,
          degerAlani: s.degerAlani === true,
          degerBirimi: s.degerBirimi || '',
        })),
      );
      setArchivedSorular(
        ars.map((s) => ({ _id: s._id, sira: s.sira, metin: s.metin || '' })),
      );
    } catch (e) {
      notify('error', e.message);
    }
  };

  useEffect(() => {
    if (editorOpen && effectiveChecklistId) {
      loadDetail(effectiveChecklistId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- urunler ayrı effect
  }, [editorOpen, effectiveChecklistId]);

  useEffect(() => {
    if (effectiveChecklistId) {
      setSelectedUrunIds(new Set(atanmisUrunler.map((u) => String(u._id))));
    }
  }, [atanmisUrunler, effectiveChecklistId]);

  const urunFiltreli = useMemo(() => {
    const q = urunArama.trim();
    if (!q) return urunler;
    return urunler.filter((u) => {
      const hay = [u.icerikAdi, u.icerikKodu, u.malKodu, u.modelKodu].filter(Boolean).join(' ');
      return includesNormalized(hay, q);
    });
  }, [urunler, urunArama]);

  const handleKaydet = async () => {
    if (editingSilindi) return;
    const ad = formAd.trim();
    if (!ad) {
      notify('error', 'Checklist adı gerekli');
      return;
    }
    const sorularRaw = formSorular
      .map((s, i) => ({
        _id: s._id,
        sira: typeof s.sira === 'number' ? s.sira : i,
        metin: (s.metin || '').trim(),
        cagriMerkezi: s.cagriMerkezi === true,
        teknikServis: s.teknikServis === true,
        degerAlani: s.degerAlani === true,
        degerBirimi: (s.degerBirimi || '').trim(),
      }))
      .filter((s) => s.metin);
    if (sorularRaw.length === 0) {
      notify('error', 'En az bir soru gerekli');
      return;
    }
    if (!sorularRaw.some((s) => s.cagriMerkezi || s.teknikServis)) {
      notify('error', 'En az bir soruda Ç veya T seçilmeli');
      return;
    }
    const sorularPayload = sorularRaw.map((s, i) => {
      const o = {
        metin: s.metin,
        sira: s.sira != null ? s.sira : i,
        cagriMerkezi: s.cagriMerkezi,
        teknikServis: s.teknikServis,
        degerAlani: s.degerAlani,
        degerBirimi: s.degerBirimi,
      };
      if (s._id) o._id = s._id;
      return o;
    });
    setSaving(true);
    try {
      if (effectiveChecklistId) {
        const res = await updateChecklist(effectiveChecklistId, {
          ad,
          islemTipi: formIslemTipi,
          aktif: formAktif,
          sorular: sorularPayload,
        });
        if (!res?.basarili) throw new Error(res?.mesaj || 'Güncellenemedi');
        notify('success', 'Checklist güncellendi');
        await loadDetail(effectiveChecklistId);
      } else {
        const res = await createChecklist({
          ad,
          islemTipi: formIslemTipi,
          aktif: formAktif,
          sorular: sorularPayload,
        });
        if (!res?.basarili || !res.veri?._id) throw new Error(res?.mesaj || 'Oluşturulamadı');
        notify('success', 'Checklist oluşturuldu');
        const newId = res.veri._id;
        setEditorTargetId(newId);
        setSearchParams({ checklist: newId }, { replace: true });
        await loadDetail(newId);
      }
      await loadList();
    } catch (e) {
      notify('error', e.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSil = async () => {
    if (!effectiveChecklistId) return;
    const ok = await confirm({
      title: 'Checklist sil',
      message:
        'Bu checklist şablonunu silmek istediğinize emin misiniz? Listede görünmeyecek; ürün atamaları kaldırılır. (Veritabanında yumuşak silme uygulanır.)',
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) {
      return;
    }
    setSaving(true);
    try {
      const res = await deleteChecklist(effectiveChecklistId);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Silinemedi');
      notify('success', res.mesaj || 'Şablon silindi');
      const refreshed = await fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true });
      setUrunler(Array.isArray(refreshed) ? refreshed : []);
      await loadList();
      closeEditor();
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKaliciSil = async () => {
    if (!effectiveChecklistId) return;
    const ok = await confirm({
      title: 'Kalıcı sil',
      message:
        'Bu şablon veritabanından kalıcı olarak silinsin mi? Yalnızca önce silinmiş (listeden kalkmış) şablonlarda ve hiç randevuda kullanılmamış, ürün bağlantısı kalmamış kayıtlarda mümkündür.',
      variant: 'danger',
      confirmLabel: 'Kalıcı sil',
    });
    if (!ok) {
      return;
    }
    setSaving(true);
    try {
      const res = await deleteChecklistPermanently(effectiveChecklistId);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Silinemedi');
      notify('success', res.mesaj || 'Kalıcı silindi');
      closeEditor();
      await loadList();
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAtaKaydet = async () => {
    if (!effectiveChecklistId) {
      notify('error', 'Önce checklist kaydedin');
      return;
    }
    if (editingSilindi) return;
    setSaving(true);
    try {
      const res = await assignChecklistToProducts(effectiveChecklistId, [...selectedUrunIds]);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Atama başarısız');
      notify('success', res.mesaj || 'Ürün ataması güncellendi');
      const refreshed = await fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true });
      setUrunler(Array.isArray(refreshed) ? refreshed : []);
      await loadAtanmisUrunler(effectiveChecklistId);
      await loadList();
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAtanmisUrunKaldir = async (urunId) => {
    if (!effectiveChecklistId || !urunId) return;
    if (editingSilindi) return;
    const ok = await confirm({
      title: 'Atamayı kaldır',
      message: 'Bu ürünün checklist ataması kaldırılsın mı?',
      variant: 'warning',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const res = await removeProductFromChecklist(effectiveChecklistId, urunId);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Kaldırılamadı');
      notify('success', res.mesaj || 'Atama kaldırıldı');
      const refreshed = await fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true });
      setUrunler(Array.isArray(refreshed) ? refreshed : []);
      await loadAtanmisUrunler(effectiveChecklistId);
      await loadList();
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleUrun = (id) => {
    if (editingSilindi) return;
    const sid = String(id);
    setSelectedUrunIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const addSoru = () => {
    if (editingSilindi) return;
    setFormSorular((prev) => [
      ...prev,
      {
        sira: prev.length,
        metin: '',
        cagriMerkezi: true,
        teknikServis: true,
        degerAlani: false,
        degerBirimi: '',
      },
    ]);
  };

  const removeSoru = (idx) => {
    if (editingSilindi) return;
    setFormSorular((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveSoru = (idx, dir) => {
    if (editingSilindi) return;
    setFormSorular((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy.map((s, i) => ({ ...s, sira: i }));
    });
  };

  const restoreArchived = (arch) => {
    if (editingSilindi) return;
    setArchivedSorular((prev) => prev.filter((x) => x._id !== arch._id));
    setFormSorular((prev) => [
      ...prev,
      {
        _id: arch._id,
        sira: prev.length,
        metin: arch.metin || '',
        cagriMerkezi: arch.cagriMerkezi !== false,
        teknikServis: arch.teknikServis !== false,
        degerAlani: arch.degerAlani === true,
        degerBirimi: arch.degerBirimi || '',
      },
    ]);
  };

  const toggleSoruField = (idx, field) => {
    setFormSorular((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: !p[field] } : p)),
    );
  };

  const rowSil = async (row) => {
    if (row.silindi) return;
    const ok = await confirm({
      title: 'Şablonu sil',
      message: `"${row.ad}" şablonunu silmek istediğinize emin misiniz? Listede görünmeyecek; ürün atamaları kaldırılır.`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) {
      return;
    }
    setSaving(true);
    try {
      const res = await deleteChecklist(row._id);
      if (!res?.basarili) throw new Error(res?.mesaj || 'Silinemedi');
      notify('success', res.mesaj || 'Şablon silindi');
      await loadList();
      const refreshed = await fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true });
      setUrunler(Array.isArray(refreshed) ? refreshed : []);
    } catch (e) {
      notify('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="checklists-page">
      <div className="checklists-page-header">
        <h1>Checklistler</h1>
      </div>

      <div className="checklists-toolbar card-like">
        <button type="button" className="btn btn-primary" onClick={() => openEditor('new')}>
          Yeni şablon
        </button>
      </div>

      <div className="checklists-table-wrap card-like">
        {loading ? (
          <div className="muted">Yükleniyor…</div>
        ) : list.length === 0 ? (
          <div className="muted">Kayıt yok.</div>
        ) : (
          <table className="admins-table checklists-table">
            <thead>
              <tr>
                <th>Şablon adı</th>
                <th>İşlem tipi</th>
                <th>Oluşturulma</th>
                <th>Aktif soru</th>
                <th>Bağlı ürün</th>
                <th>Durum</th>
                <th className="islemler-sutunu">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => {
                const d = durumEtiketi(row);
                return (
                  <tr key={row._id}>
                    <td>{row.ad}</td>
                    <td>{row.islemTipi || 'Kurulum'}</td>
                    <td>{formatTarih(row.olusturmaTarihi)}</td>
                    <td>{row.soruSayisiAktif ?? 0}</td>
                    <td>{row.urunSayisi ?? 0}</td>
                    <td>
                      <span className={`checklists-badge ${d.sinif}`}>{d.kisa}</span>
                    </td>
                    <td className="table-actions islemler-sutunu">
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => openEditor(row._id)}
                        title="Düzenle"
                        aria-label="Düzenle"
                      >
                        <i className="fas fa-edit" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="category-action-btn delete-btn"
                        disabled={saving}
                        onClick={() => rowSil(row)}
                        title="Sil"
                        aria-label="Sil"
                      >
                        <i className="fas fa-trash" aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editorOpen && (
        <div className="checklists-modal-backdrop" role="presentation" onClick={closeEditor}>
          <div
            className="checklists-modal card-like"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checklists-modal-head">
              <h2>
                {editorTargetId === 'new'
                  ? 'Yeni şablon'
                  : editingSilindi
                    ? 'Silinmiş şablon'
                    : 'Şablonu düzenle'}
              </h2>
              <button type="button" className="checklists-modal-close" onClick={closeEditor}>
                ×
              </button>
            </div>

            {editingSilindi && (
              <p className="checklists-archive-banner">
                Bu şablon silindiği için normal listede görünmez. Salt okunur; yalnızca koşullar uygunsa kalıcı
                silebilirsiniz. Kapatıp listeden devam edebilirsiniz.
              </p>
            )}

            <div className="tabs checklists-modal-tabs" role="tablist" aria-label="Checklist düzenleme">
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === 'sablon'}
                className={`tab-btn${modalTab === 'sablon' ? ' active' : ''}`}
                onClick={() => setModalTab('sablon')}
              >
                Checklist şablonu
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === 'atama'}
                className={`tab-btn${modalTab === 'atama' ? ' active' : ''}`}
                onClick={() => setModalTab('atama')}
              >
                Ürün ataması
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={modalTab === 'eklenen'}
                className={`tab-btn${modalTab === 'eklenen' ? ' active' : ''}`}
                onClick={() => setModalTab('eklenen')}
              >
                Eklenen ürünler
              </button>
            </div>

            <div className="checklists-modal-tab-panel">
              {modalTab === 'sablon' && (
                <>
                  <div className="form-row">
                    <label htmlFor="cl-ad">Ad</label>
                    <input
                      id="cl-ad"
                      className="form-control"
                      value={formAd}
                      onChange={(e) => setFormAd(e.target.value)}
                      placeholder="Örn. Kurulum öncesi kontrol"
                      disabled={editingSilindi}
                    />
                  </div>
                  <div className="form-row">
                    <label htmlFor="cl-islem-tipi">İşlem tipi</label>
                    <select
                      id="cl-islem-tipi"
                      className="form-control"
                      value={formIslemTipi}
                      onChange={(e) => setFormIslemTipi(e.target.value)}
                      disabled={editingSilindi}
                    >
                      {CHECKLIST_ISLEM_TIPLERI.map((tip) => (
                        <option key={tip} value={tip}>{tip}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-row inline">
                    <label>
                      <input
                        type="checkbox"
                        checked={formAktif}
                        onChange={(e) => setFormAktif(e.target.checked)}
                        disabled={editingSilindi}
                      />{' '}
                      Aktif
                    </label>
                  </div>
                  <div className="checklists-sorular-head">
                    <span>
                      Sorular
                      <span className="checklists-sorular-legend">
                        Ç Çağrı merkezi · T Teknik servis · D Değer alanı
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={addSoru}
                      disabled={editingSilindi}
                    >
                      Soru ekle
                    </button>
                  </div>
                  <ul className="checklists-sorular">
                    {formSorular.map((s, idx) => (
                      <li key={s._id || `new-${idx}`} className="checklists-soru-row-wrap">
                        <div className="checklists-soru-row">
                          <button
                            type="button"
                            className="icon-btn"
                            title="Yukarı"
                            onClick={() => moveSoru(idx, -1)}
                            disabled={editingSilindi || idx === 0}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            title="Aşağı"
                            onClick={() => moveSoru(idx, 1)}
                            disabled={editingSilindi || idx === formSorular.length - 1}
                          >
                            ↓
                          </button>
                          <input
                            className="form-control flex-grow"
                            value={s.metin}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFormSorular((prev) =>
                                prev.map((p, i) => (i === idx ? { ...p, metin: v } : p)),
                              );
                            }}
                            placeholder="Soru metni"
                            disabled={editingSilindi}
                          />
                          <div className="checklists-hedef-pills">
                            <button
                              type="button"
                              className={`checklists-hedef-pill checklists-hedef-pill--c ${s.cagriMerkezi ? 'is-active' : ''}`}
                              onClick={() => toggleSoruField(idx, 'cagriMerkezi')}
                              disabled={editingSilindi}
                              title="Çağrı merkezi"
                            >
                              Ç
                            </button>
                            <button
                              type="button"
                              className={`checklists-hedef-pill checklists-hedef-pill--t ${s.teknikServis ? 'is-active' : ''}`}
                              onClick={() => toggleSoruField(idx, 'teknikServis')}
                              disabled={editingSilindi}
                              title="Teknik servis"
                            >
                              T
                            </button>
                            <button
                              type="button"
                              className={`checklists-hedef-pill checklists-hedef-pill--d ${s.degerAlani ? 'is-active' : ''}`}
                              onClick={() => toggleSoruField(idx, 'degerAlani')}
                              disabled={editingSilindi}
                              title="Değer alanı"
                            >
                              D
                            </button>
                          </div>
                          {s.degerAlani && (
                            <input
                              className="form-control checklists-birim-input"
                              value={s.degerBirimi || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFormSorular((prev) =>
                                  prev.map((p, i) => (i === idx ? { ...p, degerBirimi: v } : p)),
                                );
                              }}
                              placeholder="cm"
                              disabled={editingSilindi}
                            />
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeSoru(idx)}
                            disabled={editingSilindi}
                          >
                            Listeden çıkar
                          </button>
                        </div>
                        {!s.cagriMerkezi && !s.teknikServis && (
                          <p className="checklists-soru-warn">Pasif — en az Ç veya T seçin</p>
                        )}
                      </li>
                    ))}
                  </ul>

                  {archivedSorular.length > 0 && (
                    <div className="checklists-archived-block">
                      <div className="checklists-sorular-head">
                        <span>Kaldırılan sorular (geri yükle)</span>
                      </div>
                      <ul className="checklists-archived-list">
                        {archivedSorular.map((a) => (
                          <li key={a._id} className="checklists-archived-row">
                            <span className="checklists-archived-metin">{a.metin}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => restoreArchived(a)}
                              disabled={editingSilindi}
                            >
                              Geri yükle
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {modalTab === 'atama' && (
                <section className="checklists-section-inline checklists-tab-section">
                  {!effectiveChecklistId ? (
                    <p className="muted checklists-tab-hint">
                      Önce <strong>Checklist şablonu</strong> sekmesinden şablonu kaydedin; ardından ürün ataması
                      yapabilirsiniz.
                    </p>
                  ) : (
                    <>
                      <p className="muted small">
                        Bu şablona bağlanacak ürünleri işaretleyin. Kayıtta diğer şablondan otomatik çıkarılır.
                      </p>
                      <input
                        className="form-control"
                        placeholder="Ürün ara (ad, kod…)"
                        value={urunArama}
                        onChange={(e) => setUrunArama(e.target.value)}
                        disabled={editingSilindi}
                      />
                      <div className="checklists-urun-toolbar">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={urunLoading || editingSilindi}
                          onClick={() =>
                            setSelectedUrunIds(new Set(urunFiltreli.map((u) => String(u._id))))
                          }
                        >
                          Filtrelenenin hepsini seç
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={editingSilindi}
                          onClick={() => setSelectedUrunIds(new Set())}
                        >
                          Seçimi temizle
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={saving || editingSilindi}
                          onClick={handleAtaKaydet}
                        >
                          Ürün atamasını kaydet
                        </button>
                      </div>
                      <div className="checklists-urun-list">
                        {urunLoading ? (
                          <div className="muted">Ürünler yükleniyor…</div>
                        ) : (
                          urunFiltreli.map((u) => {
                            const idStr = String(u._id);
                            const checked = selectedUrunIds.has(idStr);
                            return (
                              <label key={u._id} className="checklists-urun-item">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={editingSilindi}
                                  onChange={() => toggleUrun(u._id)}
                                />
                                <span>
                                  {u.icerikKodu} — {u.icerikAdi}
                                  {u.checklistId &&
                                    String(u.checklistId) !== String(effectiveChecklistId) && (
                                      <span className="badge-other"> (başka şablona bağlı)</span>
                                    )}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </section>
              )}

              {modalTab === 'eklenen' && (
                <section className="checklists-section-inline checklists-tab-section">
                  {!effectiveChecklistId ? (
                    <p className="muted checklists-tab-hint">
                      Önce şablonu kaydedin; ardından bu sekmede bağlı ürünleri görebilirsiniz.
                    </p>
                  ) : (
                    <>
                      <p className="muted small">
                        Bu şablona bağlı ürünler. <strong>Kaldır</strong> yalnızca checklist bağını siler.
                      </p>
                      {atanmisLoading ? (
                        <div className="muted">Yükleniyor…</div>
                      ) : atanmisUrunler.length === 0 ? (
                        <div className="muted">Henüz bu şablona bağlı ürün yok.</div>
                      ) : (
                        <ul className="checklists-assigned-list">
                          {atanmisUrunler.map((u) => (
                            <li key={u._id} className="checklists-assigned-row">
                              <div className="checklists-assigned-meta">
                                <span className="checklists-assigned-code">{u.icerikKodu}</span>
                                <span className="checklists-assigned-name">{u.icerikAdi}</span>
                                {!u.aktif && <span className="badge-inactive">Pasif ürün</span>}
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                disabled={saving || editingSilindi}
                                onClick={() => handleAtanmisUrunKaldir(u._id)}
                              >
                                Kaldır
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </section>
              )}
            </div>

            <div className="checklists-actions checklists-modal-actions">
              <button type="button" className="btn btn-primary" disabled={saving || editingSilindi} onClick={handleKaydet}>
                Kaydet
              </button>
              {effectiveChecklistId && !editingSilindi && (
                <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={handleSil}>
                  Sil
                </button>
              )}
              {effectiveChecklistId && editingSilindi && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={saving}
                  onClick={handleKaliciSil}
                >
                  Kalıcı sil
                </button>
              )}
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={closeEditor}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistsPage;
